/* eslint-disable react/no-danger */
import React from "react";
import { useTranslations } from "next-intl";
import ExpandableListSection from "@/components/common/expandable-list-section";
import { TourSummary } from "@/types/dto/pdp/pdp-tour-summary";
import { Box, Stack, SxProps, Theme, Typography, useMediaQuery } from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import TempleBuddhistOutlinedIcon from "@mui/icons-material/TempleBuddhistOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

type InclusionsFeaturedExperiencesProps = Pick<
  TourSummary,
  | "totalMeals"
  | "totalBreakfasts"
  | "totalLunches"
  | "totalDinners"
  | "accommodationCount"
  | "cultural"
  | "culinary"
  | "highlights"
  | "mustSee"
> & {
  customSx?: SxProps<Theme>;
  headingTestId?: string;
};

const InclusionsFeaturedExperiences = ({
  totalMeals,
  totalBreakfasts,
  totalLunches,
  totalDinners,
  accommodationCount,
  cultural,
  culinary,
  highlights,
  mustSee,
  customSx,
  headingTestId = "inclusions-heading"
}: InclusionsFeaturedExperiencesProps) => {
  const t = useTranslations("PDP");
  const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const mealCounts = [
    { count: totalBreakfasts, label: t("Breakfasts") },
    { count: totalLunches, label: t("Lunches") },
    { count: totalDinners, label: t("Dinners") }
  ]
    .filter(({ count }) => Number(count) > 0)
    .map(({ count, label }) => `${count} ${label}`);

  return (
    <Stack className={isBelowMd ? "fluid-box" : ""} spacing={3} sx={customSx} data-testid="inclusions-featured-experiences-container">
      <Typography data-testid={headingTestId} variant="h5">
        {t("Inclusions & Featured Experiences")}
      </Typography>

      <Box
        data-testid="inclusions-grid"
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 5 }}
      >
        {/* Item 1 - Meals */}
        <Box data-testid="meals-section" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ bgcolor: "background.cool_strong", borderRadius: "50%", p: "0.8rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <RestaurantIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Stack>
            <Typography data-testid="meals" variant="h6" sx={{ lineHeight: "1.3 !important", fontWeight: "600 !important" }}>Meals: {totalMeals}</Typography>
            <Typography data-testid="meals-count" sx={{ fontSize: "1.6rem", fontWeight: 400 }}>{mealCounts.join(", ")}</Typography>
          </Stack>
        </Box>

        {/* Item 2 - Accommodations */}
        <Box data-testid="accommodations-section" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ bgcolor: "background.cool_strong", borderRadius: "50%", p: "0.8rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <HotelOutlinedIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Stack>
            <Typography data-testid="accommodations-header" variant="h6" sx={{ lineHeight: "1.3 !important", fontWeight: "600 !important" }}>{t("Accommodations")}</Typography>
            <Typography data-testid="accommodations-count" sx={{ fontSize: "1.6rem", fontWeight: 400 }}>{accommodationCount} {accommodationCount === 1 ? t("Handpicked Accommodation") : t("Handpicked Accommodations")}</Typography>
          </Stack>
        </Box>

        {/* Item 3 - Cultural Experiences */}
        {cultural && cultural.length > 0 && (
          <ExpandableListSection
            testId="cultural-experiences-section"
            title={t("Cultural Experiences")}
            items={cultural}
            icon={<TempleBuddhistOutlinedIcon sx={{ fontSize: "22px" }} />}
            itemsToShowInitially={3}
            renderItemContent={(item) => <div dangerouslySetInnerHTML={{ __html: item }} />}
            getItemKey={(item) => item}
          />
        )}

        {/* Item 4 - Culinary Inclusions */}
        {culinary && culinary.length > 0 && (
          <ExpandableListSection
            testId="culinary-inclusions-section"
            title={t("Culinary Inclusions")}
            items={culinary}
            icon={<RestaurantMenuOutlinedIcon sx={{ fontSize: "22px" }} />}
            itemsToShowInitially={3}
            renderItemContent={(item) => <div dangerouslySetInnerHTML={{ __html: item }} />}
            getItemKey={(item) => item}
          />
        )}

        {/* Item 5 - Highlights */}
        {highlights && highlights.length > 0 && (
          <ExpandableListSection
            testId="highlights-section"
            title={t("Highlights")}
            items={highlights}
            icon={<StarBorderOutlinedIcon sx={{ fontSize: "22px" }} />}
            itemsSpacing={1}
            enableExpandCollapse={false}
            renderItemContent={(item) => <div dangerouslySetInnerHTML={{ __html: item }} />}
            getItemKey={(item) => item}
          />
        )}

        {/* Item 6 - Must-See Inclusions */}
        {mustSee && mustSee.length > 0 && (
          <ExpandableListSection
            testId="must-see-inclusions-section"
            title={t("Must-See Inclusions")}
            items={mustSee}
            icon={<CheckCircleOutlineOutlinedIcon sx={{ fontSize: "22px" }} />}
            renderItemContent={(item) => <div dangerouslySetInnerHTML={{ __html: item }} />}
            itemsToShowInitially={3}
            getItemKey={(item) => item}
          />
        )}
      </Box>
    </Stack>
  );
};

export default InclusionsFeaturedExperiences;
